import { useEffect, useState } from 'react';
import type { AppLang } from './ClientApp';
import { disablePush, enablePush, pushAvailable, savedPrefs, type EnableResult, type PushPrefs } from './push';

/**
 * «Last recommendation from <therapist>» in the client app (owner 02.11 / 04.11.2026): a row on the
 * home screen — always there; new clients see that it appears after the first visit — and a page
 * with what the therapist noticed, the treatments she suggests, her words and «Book».
 * Read from /v1/me/recommendation with the sign-in of «My bookings». Plus the notification settings.
 */
export interface MyRecommendation {
  date: string;
  staffName?: string;
  studio?: string;
  locationId: string;
  noticed: string[];
  suggested: string[];
  text: string;
  products: string[];
  nextInWeeks?: number;
  comeBackBy?: string;
}

export type RecoState =
  | { kind: 'signed_out' }
  | { kind: 'loading' }
  | { kind: 'none' }
  | { kind: 'error' }
  | { kind: 'ready'; r: MyRecommendation };

const INTL: Record<AppLang, string> = { pt: 'pt-PT', en: 'en-GB', fr: 'fr-FR', es: 'es-ES', ru: 'ru-RU' };
const ME_TOKEN = 'hub-me-token';

function meToken(): string | undefined {
  try {
    return window.localStorage.getItem(ME_TOKEN) ?? undefined;
  } catch {
    return undefined;
  }
}

function day(iso: string, lang: AppLang): string {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return new Intl.DateTimeFormat(INTL[lang], { day: 'numeric', month: 'long', timeZone: 'UTC' }).format(
    new Date(Date.UTC(y!, m! - 1, d!)),
  );
}

export function useMyRecommendation(fetchImpl: typeof fetch, lang: AppLang): RecoState {
  const [s, setS] = useState<RecoState>(() => (meToken() ? { kind: 'loading' } : { kind: 'signed_out' }));
  useEffect(() => {
    const t = meToken();
    if (!t) return setS({ kind: 'signed_out' });
    let live = true;
    fetchImpl(`/v1/me/recommendation?lang=${lang}`, { headers: { Authorization: `Bearer ${t}` } })
      .then(async (r) => {
        if (!live) return;
        if (r.status === 401) return setS({ kind: 'signed_out' });
        if (!r.ok) return setS({ kind: 'error' });
        const b = (await r.json()) as { data?: { recommendation?: MyRecommendation | null } };
        const rec = b.data?.recommendation;
        setS(rec ? { kind: 'ready', r: rec } : { kind: 'none' });
      })
      .catch(() => live && setS({ kind: 'error' }));
    return () => {
      live = false;
    };
  }, [fetchImpl, lang]);
  return s;
}

const R = {
  pt: {
    row: (who?: string) => (who ? `Última recomendação de ${who}` : 'Última recomendação'),
    rowNone: 'Recomendações da terapeuta',
    rowNoneSub: 'Aparecem aqui depois da sua visita',
    rowOut: 'Entre para ver as recomendações da sua terapeuta',
    rowError: 'Não foi possível carregar agora',
    title: 'Recomendações',
    from: (who: string) => `de ${who}`,
    noticed: 'O que notámos',
    suggested: 'Tratamentos que sugerimos',
    words: 'Da sua terapeuta',
    products: 'Para casa',
    comeBack: (d: string) => `Volte por volta de ${d}`,
    book: 'Marcar',
    back: 'Voltar',
    notif: 'Notificações',
    notifSub: 'Recomendações · horários livres',
    notifTitle: 'Notificações',
    notifReco: 'Recomendações após a visita',
    notifRecoSub: 'Cerca de 2 horas depois do tratamento',
    notifRecoSignIn: 'Entre em «As minhas marcações» para receber as suas recomendações.',
    notifHot: '🔥 Horários livres de hoje',
    notifHotSub: 'No máximo uma vez por dia. Pode desligar a qualquer momento.',
    notifStudios: 'Estúdios',
    save: 'Guardar',
    saved: 'Guardado ✓',
    denied: 'As notificações estão desligadas nas definições do telemóvel. Ative-as em Definições → 360°FACE.',
    failed: 'Não foi possível guardar. Tente de novo.',
    old: 'Atualize a aplicação para receber notificações.',
  },
  en: {
    row: (who?: string) => (who ? `Last recommendation from ${who}` : 'Last recommendation'),
    rowNone: 'Recommendations from your therapist',
    rowNoneSub: 'They appear here after your visit',
    rowOut: 'Sign in to see your therapist’s recommendations',
    rowError: 'Could not load just now',
    title: 'Recommendations',
    from: (who: string) => `from ${who}`,
    noticed: 'What we noticed',
    suggested: 'Treatments we suggest',
    words: 'From your therapist',
    products: 'For home care',
    comeBack: (d: string) => `Come back around ${d}`,
    book: 'Book',
    back: 'Back',
    notif: 'Notifications',
    notifSub: 'Recommendations · free times',
    notifTitle: 'Notifications',
    notifReco: 'Recommendations after the visit',
    notifRecoSub: 'About 2 hours after the treatment',
    notifRecoSignIn: 'Sign in under «My bookings» to get your recommendations.',
    notifHot: '🔥 Free times today',
    notifHotSub: 'At most once a day. You can switch it off any time.',
    notifStudios: 'Studios',
    save: 'Save',
    saved: 'Saved ✓',
    denied: 'Notifications are off in the phone settings. Turn them on in Settings → 360°FACE.',
    failed: 'Could not save. Please try again.',
    old: 'Update the app to get notifications.',
  },
  fr: {
    row: (who?: string) => (who ? `Dernière recommandation de ${who}` : 'Dernière recommandation'),
    rowNone: 'Recommandations de votre thérapeute',
    rowNoneSub: 'Elles apparaissent ici après votre visite',
    rowOut: 'Connectez-vous pour voir les recommandations',
    rowError: 'Chargement impossible pour le moment',
    title: 'Recommandations',
    from: (who: string) => `de ${who}`,
    noticed: 'Ce que nous avons remarqué',
    suggested: 'Soins que nous suggérons',
    words: 'De votre thérapeute',
    products: 'Pour la maison',
    comeBack: (d: string) => `Revenez vers le ${d}`,
    book: 'Réserver',
    back: 'Retour',
    notif: 'Notifications',
    notifSub: 'Recommandations · créneaux libres',
    notifTitle: 'Notifications',
    notifReco: 'Recommandations après la visite',
    notifRecoSub: 'Environ 2 heures après le soin',
    notifRecoSignIn: 'Connectez-vous dans «Mes rendez-vous» pour recevoir vos recommandations.',
    notifHot: '🔥 Créneaux libres aujourd’hui',
    notifHotSub: 'Au maximum une fois par jour. Désactivable à tout moment.',
    notifStudios: 'Studios',
    save: 'Enregistrer',
    saved: 'Enregistré ✓',
    denied: 'Les notifications sont désactivées dans les réglages du téléphone (Réglages → 360°FACE).',
    failed: 'Impossible d’enregistrer. Réessayez.',
    old: 'Mettez l’application à jour pour recevoir les notifications.',
  },
  es: {
    row: (who?: string) => (who ? `Última recomendación de ${who}` : 'Última recomendación'),
    rowNone: 'Recomendaciones de su terapeuta',
    rowNoneSub: 'Aparecen aquí después de su visita',
    rowOut: 'Inicie sesión para ver las recomendaciones',
    rowError: 'No se pudo cargar ahora',
    title: 'Recomendaciones',
    from: (who: string) => `de ${who}`,
    noticed: 'Lo que notamos',
    suggested: 'Tratamientos que sugerimos',
    words: 'De su terapeuta',
    products: 'Para casa',
    comeBack: (d: string) => `Vuelva hacia el ${d}`,
    book: 'Reservar',
    back: 'Atrás',
    notif: 'Notificaciones',
    notifSub: 'Recomendaciones · horas libres',
    notifTitle: 'Notificaciones',
    notifReco: 'Recomendaciones tras la visita',
    notifRecoSub: 'Unas 2 horas después del tratamiento',
    notifRecoSignIn: 'Inicie sesión en «Mis citas» para recibir sus recomendaciones.',
    notifHot: '🔥 Horas libres de hoy',
    notifHotSub: 'Como máximo una vez al día. Puede desactivarlo cuando quiera.',
    notifStudios: 'Estudios',
    save: 'Guardar',
    saved: 'Guardado ✓',
    denied: 'Las notificaciones están desactivadas en los ajustes del teléfono (Ajustes → 360°FACE).',
    failed: 'No se pudo guardar. Inténtelo de nuevo.',
    old: 'Actualice la aplicación para recibir notificaciones.',
  },
  ru: {
    row: (who?: string) => (who ? `Последняя рекомендация от ${who}` : 'Последняя рекомендация'),
    rowNone: 'Рекомендации мастера',
    rowNoneSub: 'Появятся здесь после вашего визита',
    rowOut: 'Войдите, чтобы увидеть рекомендации мастера',
    rowError: 'Сейчас не удалось загрузить',
    title: 'Рекомендации',
    from: (who: string) => `от ${who}`,
    noticed: 'На что мы обратили внимание',
    suggested: 'Рекомендуем процедуры',
    words: 'От вашего мастера',
    products: 'Для домашнего ухода',
    comeBack: (d: string) => `Приходите примерно ${d}`,
    book: 'Записаться',
    back: 'Назад',
    notif: 'Уведомления',
    notifSub: 'Рекомендации · свободное время',
    notifTitle: 'Уведомления',
    notifReco: 'Рекомендации после визита',
    notifRecoSub: 'Примерно через 2 часа после процедуры',
    notifRecoSignIn: 'Войдите в «Мои записи», чтобы получать свои рекомендации.',
    notifHot: '🔥 Свободное время сегодня',
    notifHotSub: 'Не чаще раза в день. Можно выключить в любой момент.',
    notifStudios: 'Студии',
    save: 'Сохранить',
    saved: 'Сохранено ✓',
    denied: 'Уведомления выключены в настройках телефона. Включите: Настройки → 360°FACE.',
    failed: 'Не удалось сохранить. Попробуйте ещё раз.',
    old: 'Обновите приложение, чтобы получать уведомления.',
  },
} as const;

/** The row on the home screen (always shown). */
export function RecommendationRow({
  s,
  lang,
  onOpen,
  onSignIn,
}: {
  s: RecoState;
  lang: AppLang;
  onOpen: () => void;
  onSignIn: () => void;
}) {
  const t = R[lang];
  const ready = s.kind === 'ready';
  const title = ready ? t.row(s.r.staffName) : t.rowNone;
  const sub =
    s.kind === 'ready'
      ? [day(s.r.date, lang), s.r.studio?.replace(/^360°FACE\s*/, '')].filter(Boolean).join(' · ')
      : s.kind === 'signed_out'
        ? t.rowOut
        : s.kind === 'error'
          ? t.rowError
          : s.kind === 'loading'
            ? '…'
            : t.rowNoneSub;
  return (
    <button
      type="button"
      className={ready ? 'ca3-card ca3-reco' : 'ca3-card ca3-reco empty'}
      onClick={s.kind === 'signed_out' ? onSignIn : ready ? onOpen : undefined}
      aria-disabled={!ready && s.kind !== 'signed_out'}
    >
      <img src="/v3/s-korugi.jpg" alt="" />
      <span>
        <b>{title}</b>
        <small>{sub}</small>
      </span>
      {(ready || s.kind === 'signed_out') && (
        <span className="ca3-arrow" aria-hidden>
          ›
        </span>
      )}
    </button>
  );
}

export function RecommendationPage({
  r,
  lang,
  onBook,
  onBack,
}: {
  r: MyRecommendation;
  lang: AppLang;
  onBook: () => void;
  onBack: () => void;
}) {
  const t = R[lang];
  return (
    <main className="ca3">
      <header className="ca3-top">
        <a className="ca3-logo" href="/app" aria-label="360°FACE">
          360<i>°</i>FACE
        </a>
      </header>
      <h1 className="ca3-h1">{t.title}</h1>
      <section className="ca3-recocard" aria-label={t.title}>
        <img className="ca3-memart" src="/v3/mark-white.png" alt="" />
        <small>{day(r.date, lang)}</small>
        <b>{r.staffName ? t.from(r.staffName) : t.title}</b>
        {r.studio && <span className="ca3-memfoot">{r.studio}</span>}
      </section>
      {r.noticed.length > 0 && (
        <>
          <h2 className="ca3-sec">{t.noticed}</h2>
          <div className="ca3-tags">
            {r.noticed.map((x) => (
              <span key={x}>{x}</span>
            ))}
          </div>
        </>
      )}
      {r.suggested.length > 0 && (
        <>
          <h2 className="ca3-sec">{t.suggested}</h2>
          <ul className="ca3-incl">
            {r.suggested.map((x) => (
              <li key={x}>
                <span>{x}</span>
              </li>
            ))}
          </ul>
        </>
      )}
      {r.text && (
        <>
          <h2 className="ca3-sec">{t.words}</h2>
          <p className="ca3-words">{r.text}</p>
        </>
      )}
      {r.products.length > 0 && (
        <>
          <h2 className="ca3-sec">{t.products}</h2>
          <ul className="ca3-incl">
            {r.products.map((x) => (
              <li key={x}>
                <span>🛍 {x}</span>
              </li>
            ))}
          </ul>
        </>
      )}
      {r.comeBackBy && <p className="ca3-comeback">{t.comeBack(day(r.comeBackBy, lang))}</p>}
      <div className="ca3-rail ca3-rail-col">
        <button type="button" className="ca3-btn red" onClick={onBook}>
          {t.book}
        </button>
        <button type="button" className="ca3-btn" onClick={onBack}>
          {t.back}
        </button>
      </div>
    </main>
  );
}

/** The notifications row (only inside the App Store / Google Play app). */
export function NotificationsRow({ lang, onOpen }: { lang: AppLang; onOpen: () => void }) {
  const t = R[lang];
  return (
    <button type="button" className="ca3-card ca3-notif" onClick={onOpen}>
      <span className="ca3-bell" aria-hidden>
        🔔
      </span>
      <span>
        <b>{t.notif}</b>
        <small>{t.notifSub}</small>
      </span>
      <span className="ca3-arrow" aria-hidden>
        ›
      </span>
    </button>
  );
}

export function NotificationsPage({
  lang,
  studios,
  signedIn,
  onBack,
  fetchImpl,
  enable = enablePush,
  disable = disablePush,
  available = pushAvailable(),
}: {
  lang: AppLang;
  studios: Array<{ id: string; name: string }>;
  signedIn: boolean;
  onBack: () => void;
  fetchImpl: typeof fetch;
  enable?: typeof enablePush;
  disable?: typeof disablePush;
  available?: boolean;
}) {
  const t = R[lang];
  const [prefs, setPrefs] = useState<PushPrefs>(
    () => savedPrefs() ?? { reco: signedIn, hot: false, locations: studios.map((s) => s.id).slice(0, 1) },
  );
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState<EnableResult | 'off'>();
  const sw = (on: boolean, flip: () => void, label: string, sub: string, disabled = false) => (
    <button
      type="button"
      className={on ? 'ca3-switch on' : 'ca3-switch'}
      role="switch"
      aria-checked={on}
      disabled={disabled}
      onClick={flip}
    >
      <span>
        <b>{label}</b>
        <small>{sub}</small>
      </span>
      <i aria-hidden />
    </button>
  );
  const save = async () => {
    setBusy(true);
    setRes(undefined);
    const p = { ...prefs, reco: prefs.reco && signedIn, locations: prefs.hot ? prefs.locations : [] };
    if (!p.reco && !p.hot) {
      await disable(fetchImpl);
      setRes('off');
    } else setRes(await enable(p, lang, fetchImpl));
    setBusy(false);
  };
  return (
    <main className="ca3">
      <header className="ca3-top">
        <a className="ca3-logo" href="/app" aria-label="360°FACE">
          360<i>°</i>FACE
        </a>
      </header>
      <h1 className="ca3-h1">{t.notifTitle}</h1>
      {!available && <p className="ca3-note ca3-warn">{t.old}</p>}
      <div className="ca3-list">
        {sw(
          prefs.reco && signedIn,
          () => setPrefs((p) => ({ ...p, reco: !p.reco })),
          t.notifReco,
          signedIn ? t.notifRecoSub : t.notifRecoSignIn,
          !signedIn,
        )}
        {sw(
          prefs.hot,
          () =>
            setPrefs((p) => ({ ...p, hot: !p.hot, locations: p.locations.length ? p.locations : [studios[0]!.id] })),
          t.notifHot,
          t.notifHotSub,
        )}
        {prefs.hot && (
          <div className="ca3-studiopick" role="group" aria-label={t.notifStudios}>
            {studios.map((s) => {
              const on = prefs.locations.includes(s.id);
              return (
                <button
                  key={s.id}
                  type="button"
                  className={on ? 'ca3-chip on' : 'ca3-chip'}
                  aria-pressed={on}
                  onClick={() =>
                    setPrefs((p) => ({
                      ...p,
                      locations: on ? p.locations.filter((x) => x !== s.id) : [...p.locations, s.id],
                    }))
                  }
                >
                  {on ? '✓ ' : ''}
                  {s.name}
                </button>
              );
            })}
          </div>
        )}
      </div>
      {res && res !== 'ok' && res !== 'off' && (
        <p className="ca3-note ca3-warn">{res === 'denied' ? t.denied : res === 'unavailable' ? t.old : t.failed}</p>
      )}
      <div className="ca3-rail ca3-rail-col">
        <button
          type="button"
          className="ca3-btn red"
          onClick={() => void save()}
          disabled={busy || !available || (prefs.hot && prefs.locations.length === 0)}
        >
          {res === 'ok' || res === 'off' ? t.saved : t.save}
        </button>
        <button type="button" className="ca3-btn" onClick={onBack}>
          {t.back}
        </button>
      </div>
    </main>
  );
}
